var SCM = SCM || {};

SCM.CrossFrameMessenger = (function () {
    
    function create(options) {
        const _options = _mergeOptions(options);
        let _logger;
        
        // Defensive logger initialization
        try {
            _logger = SCM.DebugLogger.create('CrossFrameMessenger', { debugMode: _options.debugMode || false });
        } catch(e) {
            _logger = { debug: function(){}, info: function(){}, warn: function(){}, error: function(){} };
        }
        const _state = {
            connected: false,
            connecting: false,
            reconnecting: false,
            reconnectAttempts: 0,
            messageQueue: [],
            pendingMessages: {},
            messageId: 0
        };
        let _listeners = {};
        let _reconnectTimeout = null;
        let _messageListener = null;

        function connect() {
            return new Promise(function(resolve, reject) {
                if (_state.connected) {
                    resolve();
                    return;
                }

                if (_state.connecting) {
                    reject(new Error('Connection already in progress'));
                    return;
                }

                _state.connecting = true;
                _logger.info('Starting connection establishment', { mode: _options.mode });

                _setupMessageListener();
                
                if (_options.mode === 'parent') {
                    // Parent mode - skip handshake and assume connection is ready
                    _state.connected = true;
                    _state.connecting = false;
                    _state.reconnectAttempts = 0;
                    _processMessageQueue();
                    emit('connected');
                    _logger.info('Parent mode connection established');
                    resolve();
                } else {
                    // Child mode - perform handshake with parent
                    _initiateHandshake()
                        .then(function() {
                            _state.connected = true;
                            _state.connecting = false;
                            _state.reconnectAttempts = 0;
                            _processMessageQueue();
                            emit('connected');
                            _logger.info('Connection established successfully');
                            resolve();
                        })
                        .catch(function(error) {
                            _state.connecting = false;
                            _logger.error('Connection failed', { error: error.message });
                            reject(error);
                        });
                }
            });
        }

        function send(messageType, payload) {
            return new Promise(function(resolve, reject) {
                if (!_state.connected) {
                    const queuedMessage = { messageType: messageType, payload: payload, resolve: resolve, reject: reject };
                    _state.messageQueue.push(queuedMessage);
                    _logger.debug('Message queued (disconnected)', { messageType: messageType });
                    return;
                }

                const messageId = ++_state.messageId;
                const message = {
                    id: messageId,
                    type: messageType,
                    payload: payload,
                    timestamp: Date.now()
                };

                const timeoutId = setTimeout(function() {
                    delete _state.pendingMessages[messageId];
                    reject(new Error('Message timeout'));
                }, _options.timeout);

                _state.pendingMessages[messageId] = { resolve: resolve, reject: reject, timeoutId: timeoutId };

                try {
                    _postMessage(message);
                    _logger.debug('Message sent', { messageType: messageType, id: messageId });
                } catch (error) {
                    delete _state.pendingMessages[messageId];
                    clearTimeout(timeoutId);
                    reject(error);
                }
            });
        }

        function on(event, handler) {
            if (!_listeners[event]) {
                _listeners[event] = [];
            }
            _listeners[event].push(handler);
        }

        function off(event, handler) {
            if (!_listeners[event]) {
                return;
            }
            
            const index = _listeners[event].indexOf(handler);
            if (index > -1) {
                _listeners[event].splice(index, 1);
            }
        }

        function emit(event, data) {
            if (!_listeners[event]) {
                return;
            }
            
            const handlers = _listeners[event].slice();
            for (let i = 0; i < handlers.length; i++) {
                try {
                    handlers[i](data);
                } catch (error) {
                    _logger.error('Event handler error', { event: event, error: error.message });
                }
            }
        }

        function sendMessage(messageType, payload) {
            // Send unsolicited message (no response expected)
            if (!_state.connected && !_options.allowUnsolicited) {
                _logger.warn('Cannot send unsolicited message - not connected');
                return Promise.reject(new Error('Not connected'));
            }

            const message = {
                type: messageType,
                payload: payload,
                timestamp: Date.now(),
                unsolicited: true // Flag to indicate no response expected
            };

            try {
                _postMessage(message);
                _logger.debug('Unsolicited message sent', { messageType: messageType });
                return Promise.resolve();
            } catch (error) {
                _logger.error('Failed to send unsolicited message', { messageType: messageType, error: error.message });
                return Promise.reject(error);
            }
        }

        function disconnect() {
            _state.connected = false;
            _state.connecting = false;
            _clearReconnectTimeout();
            _removeMessageListener();
            _rejectPendingMessages();
            emit('disconnected');
            _logger.info('Disconnected');
        }

        function _setupMessageListener() {
            _messageListener = function(event) {
                _handleMessage(event);
            };
            window.addEventListener('message', _messageListener);
        }

        function _removeMessageListener() {
            if (_messageListener) {
                window.removeEventListener('message', _messageListener);
                _messageListener = null;
            }
        }

        function _handleMessage(event) {
            if (!_validateOrigin(event.origin)) {
                _logger.warn('Message from unauthorized origin blocked', { origin: event.origin });
                return;
            }

            if (!event.data || typeof event.data !== 'object') {
                _logger.warn('Invalid message data structure');
                return;
            }

            const data = event.data;

            if (data.type === 'handshakeResponse') {
                _handleHandshakeResponse(data);
            } else if (data.type === 'handshake' && _options.mode === 'parent') {
                // Parent receiving handshake from child - respond
                _handleHandshakeFromChild(event);
            } else if (data.id && _state.pendingMessages.hasOwnProperty(data.id)) {
                _handleMessageResponse(data);
            } else if (data.unsolicited || !data.id) {
                // Handle unsolicited messages or messages without ID
                emit('message', data);
            } else {
                emit('message', data);
            }
        }

        function _validateOrigin(origin) {
            if (_options.allowedOrigins.length === 0) {
                return true;
            }

            for (let i = 0; i < _options.allowedOrigins.length; i++) {
                if (origin === _options.allowedOrigins[i]) {
                    return true;
                }
            }
            return false;
        }

        function _initiateHandshake() {
            return new Promise(function(resolve, reject) {
                const handshakeMessage = {
                    type: 'handshake',
                    timestamp: Date.now()
                };

                const timeoutId = setTimeout(function() {
                    reject(new Error('Handshake timeout'));
                }, _options.timeout);

                _handshakeResolve = function() {
                    clearTimeout(timeoutId);
                    resolve();
                };
                _handshakeReject = function(error) {
                    clearTimeout(timeoutId);
                    reject(error);
                };

                _postMessage(handshakeMessage);
            });
        }

        function _handleHandshakeResponse(data) {
            if (data.success && _handshakeResolve) {
                _handshakeResolve();
            } else if (_handshakeReject) {
                _handshakeReject(new Error('Handshake failed'));
            }
            _handshakeResolve = null;
            _handshakeReject = null;
        }

        function _handleHandshakeFromChild(event) {
            // Parent responding to child's handshake
            const response = {
                type: 'handshakeResponse',
                success: true,
                timestamp: Date.now()
            };

            try {
                // Security: Use the event origin that was already validated
                event.source.postMessage(response, event.origin);
                _logger.info('Handshake response sent to child');
            } catch (error) {
                _logger.error('Failed to send handshake response', { error: error.message });
            }
        }

        function _handleMessageResponse(data) {
            const pending = _state.pendingMessages[data.id];
            if (pending) {
                clearTimeout(pending.timeoutId);
                delete _state.pendingMessages[data.id];

                if (data.error) {
                    pending.reject(new Error(data.error));
                } else {
                    pending.resolve(data.payload);
                }
            }
        }

        function _postMessage(message) {
            let targetWindow;
            let targetOrigin;

            // Security: Never allow wildcard origins - always require explicit origins
            if (_options.allowedOrigins.length === 0) {
                throw new Error('No allowed origins specified - security requirement');
            }
            targetOrigin = _options.allowedOrigins[0];

            if (_options.mode === 'parent') {
                // Parent mode: send to specified target window (iframe)
                targetWindow = _options.targetWindow;
                if (!targetWindow) {
                    throw new Error('No target window specified for parent mode');
                }
            } else {
                // Child mode: send to parent window (default behavior)
                if (window.parent && window.parent !== window) {
                    targetWindow = window.parent;
                } else {
                    throw new Error('No parent window available');
                }
            }

            targetWindow.postMessage(message, targetOrigin);
        }

        function _processMessageQueue() {
            const queue = _state.messageQueue.slice();
            _state.messageQueue = [];
            
            for (let i = 0; i < queue.length; i++) {
                const item = queue[i];
                send(item.messageType, item.payload)
                    .then(item.resolve)
                    .catch(item.reject);
            }
            
            if (queue.length > 0) {
                _logger.info('Processed queued messages', { count: queue.length });
            }
        }

        function _rejectPendingMessages() {
            for (var messageId in _state.pendingMessages) {
                if (_state.pendingMessages.hasOwnProperty(messageId)) {
                    var pending = _state.pendingMessages[messageId];
                    clearTimeout(pending.timeoutId);
                    pending.reject(new Error('Connection lost'));
                }
            }
            _state.pendingMessages = {};
        }

        function _clearReconnectTimeout() {
            if (_reconnectTimeout) {
                clearTimeout(_reconnectTimeout);
                _reconnectTimeout = null;
            }
        }

        // Private variables for handshake
        let _handshakeResolve = null;
        let _handshakeReject = null;

        // Return public API
        return {
            connect: connect,
            send: send,
            sendMessage: sendMessage,
            on: on,
            off: off,
            disconnect: disconnect
        };
    }

    function _mergeOptions(options) {
        const defaults = {
            allowedOrigins: [],
            timeout: 5000,
            reconnectAttempts: 3,
            reconnectDelay: 1000,
            mode: 'child', // 'child' or 'parent'
            targetWindow: null, // for parent mode, specify iframe.contentWindow
            allowUnsolicited: true // allow messages without response expected
        };
        return Object.assign({}, defaults, options || {});
    }

    // Return the create function
    return {
        create: create
    };

})();

'object' === typeof module && module.exports && (module.exports = SCM.CrossFrameMessenger);